"use client";

/**
 * @file Hero Section Component
 * @description Main visual area of the homepage, showcasing the platform's core features and value proposition
 * Contains a feature tab switching system to display different AI functionalities
 * Using "use client" directive to mark as a client component with interactive functionality
 */

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

/**
 * Hero Section Component
 * @description Visual focal area at the top of the homepage, showcasing the platform's core features
 * @returns {JSX.Element} Hero section component
 */
export default function HeroSection() {
  // Manage the index of the currently active tab
  const [activeTab, setActiveTab] = useState(0);

  /**
   * Tab data configuration
   * @description Defines three tabs, each corresponding to a major feature area
   */
  const tabs = [
    {
      id: 'pet-to-human', // Unique identifier for routing and style matching
      label: 'Pet to Human', // Display label
      image: '/images/ChatGPT-petToHuman.png', // Feature showcase image
      description: 'Transform your pet photos into stunning human-like portraits with our advanced AI', // Feature description
    }
  ];

  return (
    // Hero section container, set to relative positioning to support background decorative elements
    <section className="relative py-20 overflow-hidden">
      {/* Background decorative elements - rendering gradient bubble effects to create a lively feel */}
      <div className="absolute inset-0 z-0 opacity-30 dark:opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-400 rounded-full filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main content area, set z-10 to display it above the background layer */}
      <div className="container mx-auto px-4 relative z-10">
        {/* Title area - displaying platform name and brief proposition */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
            Pet2Human AI · Transform Your Pets into Humans
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Ever wondered what your pet would look like as a human? Our AI technology transforms your beloved pets into stunning human portraits that capture their unique personality and charm.
          </p>
        </div>

        {/* Content grid layout - divided into two columns on large screens, information on the left and image on the right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side information area */}
          <div className="space-y-8">
            {/* Tab container - card with blurred background */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-4 rounded-xl">
              {/* Tab navigation bar - a horizontal list of tabs */}
              <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700">
                {/* Iterate through all tab options and render buttons */}
                {tabs.map((tab, idx) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(idx)} // Switch active tab on click
                    className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
                      activeTab === idx  // Apply different styles based on whether it's the active tab
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                        : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content area - displaying detailed information of the currently selected tab */}
              <div className="mt-4 p-2">
                {/* Tab title */}
                <div className="text-xl font-semibold mb-2">{tabs[activeTab].label}</div>
                {/* Tab description */}
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {tabs[activeTab].description}
                </p>
                {/* Action button - link to feature page */}
                <Link
                  href={`/${tabs[activeTab].id}`} // Directly link to the feature page
                  className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full transition-colors"
                >
                  Try Now
                  {/* Arrow icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Feature highlights grid - showcasing three main advantages */}
            <div className="grid grid-cols-3 gap-4">
              {/* Define and render three feature highlight cards */}
              {[
                { icon: '/icons/icon-fast.svg', text: 'Instant Transformation' },
                { icon: '/icons/icon-quality.svg', text: 'Personality Matching' },
                { icon: '/icons/icon-edit.svg', text: 'Multiple Styles' },
              ].map((item, idx) => (
                // Each feature card
                <div key={idx} className="flex flex-col items-center p-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-lg">
                  {/* Icon area */}
                  <div className="w-10 h-10 relative mb-2">
                    <Image
                      src={item.icon}
                      alt={item.text}
                      width={40}
                      height={40}
                      className="dark:invert" // Invert icon color in dark mode
                    />
                  </div>
                  {/* Feature text description */}
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right side image display area - showing images related to the currently selected feature */}
          <div className="relative h-[400px] rounded-xl overflow-hidden shadow-xl">
            {/* Dynamically load the image corresponding to the current tab */}
            <Image
              src={tabs[activeTab].image} // Image source changes based on active tab
              alt={tabs[activeTab].label} // Image alt text
              fill // Fill container
              className="object-cover" // Crop image to cover container
              priority // High priority loading because this is first-screen visual content
            />
            {/* Add a gradient effect from bottom to top, making text at the bottom of the image more readable */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
}